import { isPossiblePhoneNumber } from 'react-phone-number-input';

import { IAbstractRecord } from '@models';
import { isEmpty, isValidNumber } from '@utils/validationUtils';

import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import { IDynamicFormWidgetSection } from './types';

import { cloneDeep } from 'lodash';
import DateService from 'src/core/services/DateService';

export function normalizeDynamicFormDrawerValues(
  sections: IDynamicFormWidgetSection[],
  values: IAbstractRecord
): [IAbstractRecord] {
  const outputValues: IAbstractRecord = cloneDeep(values);
  sections.forEach((section) => {
    Object.values(section.inputs).forEach((property) => {
      switch (property.type) {
        case FormFieldType.text:
          outputValues[property.name] = outputValues[property.name]
            ? outputValues[property.name].trim()
            : '';
          break;
        default:
          break;
      }
    });
  });

  return [outputValues];
}

export const validateDynamicFormWidget = (
  value: any,
  input: DynamicFormWidgetInputType,
  values: IAbstractRecord
): string | Record<string, string>[] => {
  if (input === undefined) {
    return '';
  }

  if (
    input.hidden ||
    (input.conditionalHidden && input.conditionalHidden(values))
  ) {
    return '';
  }

  if (
    (input.required ||
      (input.conditionalRequired && input.conditionalRequired(values))) &&
    isEmpty(value)
  ) {
    return 'Required';
  }

  if (
    value &&
    !(
      input.disabled ||
      (input.conditionalDisable && input.conditionalDisable(values))
    )
  ) {
    switch (input.type) {
      case FormFieldType.text: {
        if (
          input.minCharacters &&
          input.minCharacters > 0 &&
          value.length < input.minCharacters
        ) {
          return 'Min ' + input.minCharacters + ' characters';
        } else if (
          input.maxCharacters &&
          input.maxCharacters > 0 &&
          value.length > input.maxCharacters
        ) {
          return 'Max ' + input.maxCharacters + ' characters';
        }
        break;
      }

      case FormFieldType.number: {
        if (isValidNumber(input.minValue) && value < input.minValue) {
          return `Please enter a number higher than ${input.minValue}`;
        } else if (isValidNumber(input.maxValue) && value > input.maxValue) {
          return `Please enter a number lower than ${input.maxValue}`;
        }
        break;
      }
      case FormFieldType.phonenbtext: {
        // Ensure the value is a string and has exactly 8 characters
        if (typeof value !== 'string' || value.length !== 8) {
          return 'Phone number must be exactly 8 characters.';
        }
        break;
      }

      case FormFieldType.phoneNumber: {
        value = value.replace('+', '');
        if (!isPossiblePhoneNumber(`+${value}`)) {
          return 'Invalid Phone Number';
        }
        break;
      }

      case FormFieldType.datetime: {
        if (!DateService.isValidDate(value)) return 'Invalid DateTime';
        break;
      }

      case FormFieldType.date: {
        if (!DateService.isValidDate(value)) {
          return 'Invalid date';
        }

        if (input.name === 'dateOfBirth') {
          const today = new Date();
          const selectedDate = new Date(value);

          if (selectedDate > today) {
            return 'Date of birth cannot be in the future.';
          }
        }
        break;
      }

      case FormFieldType.repeaterBox: {
        const result: Record<string, string>[] = [];

        value.forEach((item: Record<string, string>, index: number) => {
          const rowErrors: Record<string, string> = {};
          Object.entries(input.inputs).forEach(([inputName, subInput]) => {
            const fieldValue = item[inputName];
            const isRequired =
              subInput.required ||
              (subInput.conditionalRequired &&
                subInput.conditionalRequired(values));

            if (
              isRequired &&
              (fieldValue === '' ||
                fieldValue === null ||
                (Array.isArray(fieldValue) && fieldValue.length === 0))
            ) {
              rowErrors[inputName] = 'Required';
            } else {
              const inputError = validateDynamicFormWidget(
                fieldValue,
                subInput,
                values
              );
              if (typeof inputError === 'string') {
                rowErrors[inputName] = inputError;
              }
            }
          });

          result.push(rowErrors);
        });

        return result;
      }
    }
    if (input.customValidator) {
      return input.customValidator(values);
    }
  }

  return '';
};
